import get from 'lodash/get'
import values from 'lodash/values'
import isEmpty from 'lodash/isEmpty'
import PCLNAnalytics from '@pcln/analytics'
import { pclnExpt as flyExpt } from '../../../common/fly-experimentation/fly-experimentation'
import { getFlexDatesExpressDeal } from '../../util/flexDatesExdHelper/flexDatesExdHelper'
import { handlePackageCrossSell } from '../../util/crossSellGenericAd'
import { buildOrderedListings } from '../../util/buildOrderedListings'
import { flySearchResponseData } from '../../models/FlySearchResponseModel'
import { getPriceForCurrentDate } from './listingsContentHelper.js'
import { fireFlyConditions } from '../../constants/fireflyCondition'

/**
 * Keep this function pure,
 * For the sake of code sanity, DO NOT modify to the paramters passed to it
 * */
export function populateListingsState({
  listingsContentState,
  listingsContentProps = {},
}) {
  const {
    travelDate,
    returnDate,
    tripType,
    sbsListings,
    // fireListingsImpressionsOnce
  } = listingsContentState
  const {
    pclnBootstrapData,
    calendarPricing,
    listingsMetaData,
    filterValues,
  } = listingsContentProps
  const listingsResponse = listingsContentProps.listings || {}
  const { response, lastUpdateTime } = listingsResponse
  let listings = response || []
  let minimumRetailPrice = listingsMetaData?.minimumRetailPrice ?? 0

  const hasExpressDeal = get(listingsMetaData, 'hasExpressDeal', false)
  const orderedListingsData = buildOrderedListings({
    itineraries: listings,
    hasExpressDeal,
    packagesEvery10thListing: flyExpt.isVariant(
      'PKG_SHORTER_FLIGHT_PATH_PROMOS'
    ),
    isRoundTrip: tripType === 'RT',
    searchRequest: pclnBootstrapData.searchRequest,
    isMobileDevice: pclnBootstrapData.isMobile,
    hasUSDestinationsOnly: pclnBootstrapData.hasUSDestinationsOnly,
    isWhiteLabel: !!pclnBootstrapData.partner,
    AIR_LISTINGS_AD_CLICK_OFF: flyExpt.isVariant('AIR_LISTINGS_AD_CLICK_OFF'),
  })
  listings = orderedListingsData.listings
  if (orderedListingsData.minimumRetailPrice) {
    minimumRetailPrice = orderedListingsData.minimumRetailPrice
  }

  const {
    airports,
    exdCarriers,
    searchSessionKey,
    priceGuidance,
  } = flySearchResponseData

  let isInternational = false

  // Populate airportToCity
  const airportToCity = {}

  if (airports) {
    /**
     * Using lodash 'values' instead of 'Object.values()' as
     * Object.values throws error on IE 11 and below
     */
    values(airports).forEach(currentAirport => {
      if (currentAirport.isoCountryCode === 'US') {
        airportToCity[
          currentAirport.code
        ] = `${currentAirport.city}, ${currentAirport.state}`
      } else {
        airportToCity[
          currentAirport.code
        ] = `${currentAirport.city}, ${currentAirport.country}`
        isInternational = true
      }
    })
  }

  // Fire Impressions (flyExpt.fireImpression())
  // if (!fireListingsImpressionsOnce && hasExpressDeal) {
  // }

  // Alternate Dates Express Deals Logic
  if (
    !fireFlyConditions.isFireFly() &&
    !exdCarriers.length &&
    tripType !== 'MD' &&
    !hasExpressDeal &&
    isEmpty(filterValues?.latestFilterChange)
  ) {
    const altExpressDeals = listingsContentProps.calendarPricingExpressDeals

    const altExd = getFlexDatesExpressDeal(
      altExpressDeals,
      tripType,
      minimumRetailPrice
    )

    if (altExd) {
      // TODO: Refactor this
      altExd.totalPriceWithDecimal = calculateTotalFare(altExd, false)

      const signInData = window.__PCLN_SI_PERSISTENCE__?.stickySignInData
      const isUserSignedIn = signInData?.passiveSignIn || signInData?.signedIn
      altExd.applyVIPDiscount = isUserSignedIn

      listings.unshift(altExd)
    }
  }

  const crossSellControlObject = handlePackageCrossSell(pclnBootstrapData)
  const priceOnCalendar = getPriceForCurrentDate(
    calendarPricing,
    travelDate,
    returnDate
  )
  if (priceOnCalendar > 0) {
    PCLNAnalytics.log({
      action: 'ELASTIC-ACCURACY',
      label: 'difference',
      value: `${priceOnCalendar - minimumRetailPrice}`,
      isInternational: `${isInternational}`,
      tripType,
    })
  }

  return {
    listings,
    searchKey: searchSessionKey,
    exdCarriers,
    airportToCity,
    showLoadingMessage: false,
    loadingSuccess: true,
    crossSellControlObject,
    fireListingsImpressionsOnce: true,
    lastUpdateTime,
    minimumRetailPrice,
    priceGuidance,
    sbsListings,
  }
}

function calculateTotalFare(itinerary, roundIt = false, upsellOption) {
  let totalFees = 0

  if (itinerary.pricingInfo.fees) {
    itinerary.pricingInfo.fees.forEach(fee => {
      totalFees += fee.amount
    })
  }
  const decimal = roundIt ? 0 : 2
  const totalFareToAdd = upsellOption
    ? get(upsellOption, 'pricingInfo.prices[0].totalFare', 0)
    : itinerary.pricingInfo.totalFare
  const totalFare = (totalFareToAdd + totalFees).toFixed(2)
  const price = roundIt ? Math.ceil(totalFare) : totalFare
  const dollarString = price.toString()

  const dollar = roundIt
    ? dollarString
    : dollarString.indexOf('.') > -1
    ? dollarString.substr(0, dollarString.indexOf('.'))
    : dollarString

  const cents = roundIt
    ? null
    : dollarString.indexOf('.') > -1
    ? dollarString.substr(dollarString.indexOf('.') + 1)
    : '00'

  const centsFormatted = cents === 0 || cents === null ? '00' : `${cents}`

  // TODO no more $filter
  // let priceFormatted = $filter('currency')(dollar, undefined, decimal);
  let priceFormatted = `${dollar}.${decimal}`

  if (!roundIt) {
    priceFormatted = priceFormatted.substr(0, priceFormatted.indexOf('.'))
  }

  return {
    dollar: priceFormatted,
    cents: centsFormatted,
    price,
  }
}
